/**
 * The three L2 views that open with a pinpoint finding (`/fo/console/finding`):
 * the shape of the cell's own loads, where the company sits among everyone else
 * running the same form, and whether the cost follows the clock.
 *
 * ## Why a drawer needs charts at all
 *
 * The board row already carries the medians, the ratio and the person-hours.
 * What it cannot carry is **shape**, and shape is what decides the ticket. Three
 * questions, three forms (design rules §2):
 *
 * 1. **Is the whole form slow, or a few sessions?** A box against the pooled
 *    peer box. The p10 is the load-bearing number: a cell whose *fastest tenth*
 *    already costs half a minute has no fast path to protect, so the cost is in
 *    the form. A cell that is quick for most people and catastrophic for a few
 *    has the same median and a completely different fix. The verdict line under
 *    the chart is computed from the two boxes, never asserted — it prints the
 *    strongest statement the numbers actually support and nothing stronger.
 * 2. **Is this company an outlier, or is the form dear everywhere?** A dot per
 *    company, sorted by median. Measured on 2026-08-22 for
 *    `SalesQuotationListPage`: eight companies between 1 234 and 1 875 ms, then
 *    nothing until 6 485, 16 877 and 23 750. The **gap** is the finding, and no
 *    ratio carries it — the same plot for `SalesQuotationTableForNew` shows
 *    eight dots strung evenly from 35 to 80 s with no gap at all, which says the
 *    opposite in the same picture. So the cluster is banded, and the band is
 *    derived from the data (the largest step between neighbours), never drawn
 *    by eye.
 * 3. **Is it the load or the work?** The median by hour of day. A saw-tooth
 *    means the form gets dearer when the system is busy; a flat line means it
 *    costs what it costs and the hour is irrelevant. `SalesQuotationTableForNew`
 *    is flat — 63 537 ms at 04:00 against 37 832 at 23:00, a 1.7× spread over a
 *    day whose *floor* is 38 seconds.
 *
 * ## Scales, stated rather than assumed
 *
 * - The box plot is drawn on a **log axis** whenever the values span 4× or more,
 *   because F&O load durations run from 1.4 s to 161 s and a linear axis spends
 *   nine tenths of its width on empty space. An unlabelled log axis is a lie of
 *   omission, so the caption always names the scale in use — including the times
 *   it stays linear.
 * - The dot plot is **linear and anchored at zero**, deliberately the opposite
 *   choice: there the empty space *is* the message, and a log axis would fold it
 *   shut. A truncated axis would do the reverse and manufacture a gap, so the
 *   axis starts at 0.
 * - The hour strip's ramp is anchored at zero too, so a form whose worst hour is
 *   1.7× its best reads as nearly one colour. Normalising min-to-max would paint
 *   a flat day in the full five-step ramp and invent a rhythm that is not there.
 *
 * ## Absence
 *
 * Every panel here can legitimately have nothing to show, and "nothing measured"
 * never renders like "measured, and it is zero" (HARD RULE 6 / design rules §5):
 *
 * - `distribution` / `distribution_peer` are `null` when the cell had too few
 *   loads *or* when the query failed. The two are told apart by `errors[]`,
 *   which the drawer renders above; neither draws a box of zeroes.
 * - `peers` is empty when nobody cleared the backend's 30-load floor. A company
 *   below the floor has **no dot**, so a missing dot is not a fast company.
 * - `hourly` carries only the hours that had a load. Twenty-four is a ceiling,
 *   not a count: an F&O cell is one company's working day, and an hour nobody
 *   opened the form draws as a hole, not as a low value. Padding it would invent
 *   a nightly recovery that never happened.
 *
 * ## Colour
 *
 * Two keys only, both through DS config: the subject wears the accent token
 * (emphasis, not status) and the peers a neutral hue from `identityConfig`,
 * which defaults to the neutral half of the ramp precisely so that
 * `--norican-chart-1..5` — Simpson green, Monitizer amber, Striko red — cannot
 * hand a severity reading to whichever series happens to be first. The hour
 * strip is a sequential ramp because magnitude is never a categorical colour
 * (§3.4), and it carries no text, so the filled-cell contrast trap that
 * `DataGrid.cellScale` documents does not apply.
 */

import { type BoxPlotDatum, Chart, type ChartConfig } from "@norican/dataviz";
import { Building2, ChartNoAxesColumn, Clock, Icon } from "@norican/icons";
import { Alert, cn, DataGrid, type DataGridColumn, EmptyState, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { type Verdict, VerdictFooter } from "@/components/board";
import {
  type FoConsoleFindingDetail,
  type FoConsoleFindingDistribution,
  type FoConsoleFindingHour,
  type FoConsoleLookback,
  type FoConsolePinpointFinding,
  useFoConsoleFinding,
} from "@/lib/api";
import { identityConfig } from "@/lib/charts/config";

import { FoAccessPanel, FoErrorsAlert, isPanelOk } from "./access-panel";
import {
  apiErrorDetail,
  apiErrorStatus,
  EM_DASH,
  formatCount,
  formatDurationCompact,
  formatDurationMs,
  formatRatio,
  heatBand,
  lookbackOption,
} from "./console-format";

/* ------------------------------------------------------------------------ *
 * Shared constants
 * ------------------------------------------------------------------------ */

/**
 * Colour for the finding's own company. The accent token, not a chart hue: this
 * is emphasis against context, which is exactly what the accent is for, and it
 * stays clear of both the business-unit colours and the reserved status tones.
 */
const SUBJECT_COLOR = "var(--norican-accent)";

/**
 * Colour for everybody else, taken from the board's own categorical helper so
 * the hue comes from DS config rather than a hand-picked `--norican-chart-N`.
 */
const PEER_COLOR = identityConfig(["peer"]).peer?.color ?? "var(--norican-fg-muted)";

/**
 * Below this spread a log axis compresses for no gain, so the box plot stays
 * linear and says so. Four is deliberately low: F&O latency usually spans two
 * orders of magnitude, and the branch exists for the cells that do not.
 */
const LOG_MIN_SPREAD = 4;

/** `log10(0)` is `-Infinity`. Durations are positive; this is a guard, not a policy. */
const MS_FLOOR = 1;

/**
 * How large the biggest step between two neighbouring company medians has to be
 * before it counts as a gap rather than a slope. Measured on
 * `SalesQuotationListPage`, the real gap is 3.5× (1 875 → 6 485 ms) against a
 * largest within-cluster step of 1.26×, so the threshold has room on both sides.
 */
const MIN_GAP_RATIO = 1.75;

/** Fewer dots than this and "a cluster and a tail" is a story about noise. */
const MIN_DOTS_FOR_BAND = 4;

/**
 * Peak-to-trough ratio at which the day stops reading as flat. Under it the
 * hour is not what makes the form dear; over it the form gets dearer when the
 * system is busy.
 */
const FLAT_SPREAD = 2;

/** Fewer measured hours than this and the shape of the day is not a finding. */
const MIN_HOURS_FOR_SHAPE = 4;
/**
 * Loads an hour needs before its median may set the peak or the trough.
 *
 * Not a display threshold — the strip draws every hour it has. This guards the
 * SENTENCE, which names a cause and routes a ticket. An F&O cell's quiet hours
 * carry one or two loads, and one load is not a median.
 */
const MIN_LOADS_FOR_SHAPE = 10;

/** Hours in a day — the strip's ceiling, never its count. */
const DAY_HOURS = 24;

/** A day in milliseconds, for deciding whether the hour strip can be read at all. */
const DAY_MS = 86_400_000;

/** The one sentence that keeps an empty hour from reading as a fast one. */
const GAP_NOT_ZERO =
  "An hour with no cell is an hour nobody opened the form, not an hour it was instant.";

/* ------------------------------------------------------------------------ *
 * Entry point
 * ------------------------------------------------------------------------ */

interface FindingChartsProps {
  /** The open finding — the drawer only mounts this with one. */
  finding: FoConsolePinpointFinding;
  /** The board's window, restated in every caption so a shared link explains itself. */
  lookback: FoConsoleLookback;
}

/**
 * The measurements behind one finding, fetched on open and only on open.
 *
 * Four queries per row against a 25-row board is a hundred queries for a drawer
 * nobody opened, which is why this is a drill rather than part of `/pinpoint` —
 * see `useFoConsoleFinding`. A finding that names no single legal entity (a
 * form-wide one) or no form (an AOS-node one) has no cell to measure, so the
 * hook stays disabled and the panel says which of the two it is.
 */
export function FindingCharts({ finding, lookback }: FindingChartsProps) {
  const addressable = finding.form !== "" && finding.company !== "";
  const query = useFoConsoleFinding(
    addressable ? finding.form : null,
    addressable ? finding.company : null,
    lookback,
  );
  const detail = query.data;
  const status = apiErrorStatus(query.error);
  const windowCaption = lookbackOption(lookback).caption;

  if (!addressable) {
    return (
      <Block
        title="How this cell measures up"
        caption={windowCaption}
        icon={<Icon icon={ChartNoAxesColumn} size="sm" aria-hidden="true" />}
      >
        <Typography variant="body-sm" tone="muted">
          {/* Branch on the KIND, not on the empty strings. An `aos_node`
              finding carries an empty form AND an empty company, so a
              `company === ""` test sent every one of them down the form-wide
              path and left this sentence unreachable — telling somebody looking
              at a node finding to "open one of the per-company findings for the
              same form", of which there is no form. */}
          {finding.kind === "aos_node"
            ? "This finding is about an AOS instance rather than a form, so there is no cell to measure. The three views below a finding are per-(form, company); a node has neither."
            : "This finding is about the form across every company, so there is no single cell to open up. The distribution, the peer plot and the hour strip are all measured for one form in one legal entity; a form-wide finding has none to name. Open one of the per-company findings for the same form to see them."}
        </Typography>
      </Block>
    );
  }

  if (query.error) {
    return (
      <Block
        title="How this cell measures up"
        caption={windowCaption}
        icon={<Icon icon={ChartNoAxesColumn} size="sm" aria-hidden="true" />}
      >
        <Alert
          tone={status === 403 ? "warning" : "danger"}
          title={
            status === 403
              ? "This sign-in is not on the investigator list"
              : "Couldn't read this cell's measurements"
          }
        >
          {apiErrorDetail(query.error) ??
            (status === 0
              ? "The NOVA API did not respond. The finding above is unaffected — it was already loaded."
              : `The NOVA API answered ${status ?? "with an error"}. The finding above stands; only these four measurements are missing.`)}
        </Alert>
      </Block>
    );
  }

  // Loading holds the frame: the three headings and three boxes of the right
  // height render immediately, so nothing below them moves when the payload
  // lands. A skeleton would flash a second layout for a query that is usually
  // a cache hit (design rules §4.5).
  if (detail == null) {
    return (
      <>
        <Block
          title="Load duration, this cell against its peers"
          caption={windowCaption}
          icon={<Icon icon={ChartNoAxesColumn} size="sm" aria-hidden="true" />}
        >
          <HoldingFrame className="h-40" />
        </Block>
        <Block
          title="Every company running this form"
          caption={windowCaption}
          icon={<Icon icon={Building2} size="sm" aria-hidden="true" />}
        >
          <HoldingFrame className="h-64" />
        </Block>
        <Block
          title="Median by hour of day"
          caption={windowCaption}
          icon={<Icon icon={Clock} size="sm" aria-hidden="true" />}
        >
          <HoldingFrame className="h-20" />
        </Block>
      </>
    );
  }

  if (!isPanelOk(detail)) {
    return (
      <Block
        title="How this cell measures up"
        caption={windowCaption}
        icon={<Icon icon={ChartNoAxesColumn} size="sm" aria-hidden="true" />}
      >
        {/* `repeated`: the board above has already explained this state in full
            for the same workspace, and saying it twice reads as two faults. */}
        <FoAccessPanel panel="This finding's measurements" state={detail} repeated />
      </Block>
    );
  }

  // A partial answer still renders. One failed query out of four leaves the
  // other three standing, and the named error is what tells "unavailable" apart
  // from "nothing found" in the panels below.
  return (
    <div className={cn("flex flex-col gap-5", query.isFetching && "opacity-70 transition-opacity")}>
      {detail.errors.length > 0 && (
        <FoErrorsAlert
          errors={detail.errors}
          title="Some of this cell's measurements are missing"
        />
      )}
      <DistributionBlock detail={detail} lookback={lookback} />
      <PeerDotsBlock detail={detail} lookback={lookback} />
      <HourStripBlock detail={detail} lookback={lookback} />
    </div>
  );
}

/* ------------------------------------------------------------------------ *
 * a. The distribution, as a box plot
 * ------------------------------------------------------------------------ */

/** Config key for the finding's own box; the container turns it into `--color-subject`. */
const BOX_SUBJECT = "subject";
/** Config key for the pooled peer box. */
const BOX_PEER = "peer";

interface DistributionBlockProps {
  detail: FoConsoleFindingDetail;
  lookback: FoConsoleLookback;
}

function DistributionBlock({ detail, lookback }: DistributionBlockProps) {
  const own = detail.distribution;
  const peer = detail.distribution_peer;
  const windowCaption = lookbackOption(lookback).caption;

  const config: ChartConfig = {
    [BOX_SUBJECT]: { label: `${detail.company_name} (${detail.company})`, color: SUBJECT_COLOR },
    [BOX_PEER]: { label: "Every other company, pooled", color: PEER_COLOR },
  };

  const boxes: BoxPlotDatum[] = [];
  if (own) {
    boxes.push({
      cell: detail.company,
      ...percentileBox(own),
      color: `var(--color-${BOX_SUBJECT})`,
    });
  }
  if (peer) {
    boxes.push({ cell: "Others", ...percentileBox(peer), color: `var(--color-${BOX_PEER})` });
  }

  const values = boxes.flatMap((box) => [
    box.min as number,
    box.q1 as number,
    box.median as number,
    box.q3 as number,
    box.max as number,
  ]);
  const lo = values.length > 0 ? Math.min(...values) : 0;
  const hi = values.length > 0 ? Math.max(...values) : 0;
  const useLog = lo > 0 && hi / lo >= LOG_MIN_SPREAD;

  const scaled = useLog
    ? boxes.map((box) => ({
        ...box,
        min: toLog(box.min as number),
        q1: toLog(box.q1 as number),
        median: toLog(box.median as number),
        q3: toLog(box.q3 as number),
        max: toLog(box.max as number),
      }))
    : boxes;
  // Padded by a fraction of the log span rather than rounded out to the nearest
  // half-decade: rounding put a box that starts at 1.0 s on an axis that starts
  // at 0.28 s, which is the same wasted width the log scale exists to reclaim.
  // Linear keeps the component's own 5 % pad.
  const logPad = Math.max((toLog(hi) - toLog(lo)) * 0.04, 0.02);
  const domain: [number, number] | undefined = useLog
    ? [toLog(lo) - logPad, toLog(hi) + logPad]
    : undefined;

  const scaleWord = useLog
    ? "logarithmic axis — each step is a multiple, not an addition"
    : "linear axis";
  const caption = `${windowCaption} · whiskers are the 10th and 90th percentiles · ${scaleWord}`;
  // `companies` counts every other company that ran the form at all, including
  // the ones below the dot plot's 30-load floor. It is this box's own n and is
  // deliberately not the number of dots in the panel below.
  const peerCompanies = peer?.companies == null ? "other" : formatCount(peer.companies);
  const floor = floorVerdict(own, peer, detail.company_name);

  return (
    <Block
      title="Load duration, this cell against its peers"
      caption={caption}
      icon={<Icon icon={ChartNoAxesColumn} size="sm" aria-hidden="true" />}
    >
      {boxes.length === 0 ? (
        <NotMeasured
          title="No distribution for this cell"
          description={`The percentile query returned nothing for ${detail.form} in ${detail.company} over the ${windowCaption}. Either too few loads to compute percentiles from, or the query failed — an error above says which. It is not a distribution of zeroes.`}
        />
      ) : (
        <>
          <Chart.Container
            config={config}
            className="h-40"
            aria-label={`Load duration for ${detail.form} in ${detail.company_name}${peer ? ", against the same form pooled across every other company that runs it" : ""}, ${windowCaption}, ${useLog ? "logarithmic" : "linear"} scale, whiskers at the 10th and 90th percentiles`}
          >
            {/* One series of two categories, so no legend: the category axis
                names each box and the title names the measure (§6). Outliers are
                off because the payload carries percentiles, not samples — there
                are no Tukey outliers to draw, and an absent dot must not read as
                "nothing beyond the fences". */}
            <Chart.BoxPlot
              data={scaled}
              categoryKey="cell"
              orientation="horizontal"
              showOutliers={false}
              domain={domain}
              valueFormatter={(value) => formatDurationMs(useLog ? fromLog(value) : value)}
            />
          </Chart.Container>

          <PercentileTable detail={detail} own={own} peer={peer} />

          <Typography variant="caption" tone="muted">
            The box is the middle half of the loads (25th to 75th percentile) and the line in it is
            the median; the whiskers stop at the 10th and 90th, so the chart&apos;s own tooltip
            labels them &quot;min&quot; and &quot;max&quot; when they are neither — the fastest and
            slowest single load are outside both ends and are not plotted.{" "}
            {peer
              ? `"Others" pools every load of this form across the ${peerCompanies} other companies that ran it, weighted by volume — which is why its median is not the peer median on the finding above, where each company gets one vote regardless of how much it used the form.`
              : "No other company ran this form in this window, so there is no box to compare against. Nobody else running it is not everybody else being instant."}
            {own == null &&
              ` The box on show is the peer box only: ${detail.company} produced no distribution of its own, so there is nothing here to compare against it.`}
          </Typography>

          {floor && <VerdictFooter verdict={floor} />}
        </>
      )}
    </Block>
  );
}

/** Five percentiles onto the five numbers a box wants. Whiskers are p10/p90, not fences. */
function percentileBox(distribution: FoConsoleFindingDistribution) {
  return {
    min: distribution.p10,
    q1: distribution.p25,
    median: distribution.p50,
    q3: distribution.p75,
    max: distribution.p90,
  };
}

function toLog(ms: number): number {
  return Math.log10(Math.max(ms, MS_FLOOR));
}

function fromLog(value: number): number {
  return 10 ** value;
}

interface PercentileRow {
  id: string;
  label: string;
  own: number | null;
  peer: number | null;
}

interface PercentileTableProps {
  detail: FoConsoleFindingDetail;
  own: FoConsoleFindingDistribution | null;
  peer: FoConsoleFindingDistribution | null;
}

/**
 * The chart's table twin (§4.4), inline rather than behind a "View data" button:
 * this drawer *is* the L2 view, and opening a second drawer on top of it to read
 * five numbers would be a worse answer than printing them.
 */
function PercentileTable({ detail, own, peer }: PercentileTableProps) {
  const rows: PercentileRow[] = [
    { id: "p10", label: "p10 · fastest tenth", own: own?.p10 ?? null, peer: peer?.p10 ?? null },
    { id: "p25", label: "p25", own: own?.p25 ?? null, peer: peer?.p25 ?? null },
    { id: "p50", label: "p50 · median", own: own?.p50 ?? null, peer: peer?.p50 ?? null },
    { id: "p75", label: "p75", own: own?.p75 ?? null, peer: peer?.p75 ?? null },
    { id: "p90", label: "p90 · slowest tenth", own: own?.p90 ?? null, peer: peer?.p90 ?? null },
  ];
  const columns: DataGridColumn<PercentileRow>[] = [
    { id: "label", header: "Percentile", accessor: (row) => row.label },
    {
      id: "own",
      header: `${detail.company} (${formatCount(own?.events)} loads)`,
      align: "end",
      accessor: (row) => row.own,
      cell: (row) => <span className="tabular-nums">{formatDurationMs(row.own)}</span>,
    },
    {
      id: "peer",
      header: `Others (${formatCount(peer?.events)} loads)`,
      align: "end",
      accessor: (row) => row.peer,
      cell: (row) => <span className="tabular-nums">{formatDurationMs(row.peer)}</span>,
    },
  ];
  return (
    <DataGrid<PercentileRow>
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      density="compact"
      aria-label={`Load-duration percentiles for ${detail.form} in ${detail.company}, against every other company pooled`}
    />
  );
}

/**
 * What the two boxes say about the **floor**, and only what they say.
 *
 * The p10 is the percentile that separates the two ways a median gets to be
 * large, so the verdict walks down from the strongest claim to the weakest and
 * stops at the first one the numbers actually support. Nothing here is a canned
 * sentence: a cell whose fastest tenth beats the pooled median gets told that
 * its problem is the tail, which is a different ticket.
 */
function floorVerdict(
  own: FoConsoleFindingDistribution | null,
  peer: FoConsoleFindingDistribution | null,
  companyName: string,
): Verdict | null {
  if (own == null || peer == null) {
    return null;
  }
  const floor = formatDurationMs(own.p10);
  if (own.p10 > peer.p90) {
    return {
      label: "No fast path",
      tone: "danger",
      text: `${companyName} is slower at its best than everyone else is at their worst: its fastest tenth costs ${floor}, above the ${formatDurationMs(peer.p90)} that only a tenth of loads elsewhere exceed. There is no quick session to protect, so this is what the form costs here.`,
    };
  }
  if (own.p10 > peer.p75) {
    return {
      label: "Floor has moved",
      tone: "danger",
      text: `Even the fastest tenth here (${floor}) is slower than three quarters of the loads everywhere else (${formatDurationMs(peer.p75)}). The whole distribution has shifted, not just its tail.`,
    };
  }
  if (own.p10 > peer.p50) {
    return {
      label: "Floor has moved",
      tone: "warning",
      text: `The fastest tenth here (${floor}) is already slower than the typical load elsewhere (${formatDurationMs(peer.p50)}), so the cost reaches every session rather than a few unlucky ones.`,
    };
  }
  return {
    label: "Tail, not floor",
    tone: "warning",
    text: `The floor is intact — the fastest tenth here (${floor}) is at or below the pooled median elsewhere (${formatDurationMs(peer.p50)}) — while the slowest tenth reaches ${formatDurationMs(own.p90)} against ${formatDurationMs(peer.p90)}. Some sessions are fine and some are not, so look for what those sessions have in common before changing the form.`,
  };
}

/* ------------------------------------------------------------------------ *
 * b. The peer dot plot
 * ------------------------------------------------------------------------ */

/** Legend / config key for the dots that are not the finding's company. */
const DOT_PEER = "peer";
/** Legend / config key for the finding's own company. */
const DOT_SUBJECT = "subject";

/** Relative dot size, mapped by the Z axis — a channel that is not colour. */
const PEER_WEIGHT = 1;
const SUBJECT_WEIGHT = 3;

interface DotRow {
  /** Position on the value-ordered axis, 0 = fastest. The category order is mine, not Recharts'. */
  rank: number;
  company: string;
  company_name: string;
  p50_ms: number;
  loads: number;
  is_subject: boolean;
  weight: number;
}

interface PeerDotsBlockProps {
  detail: FoConsoleFindingDetail;
  lookback: FoConsoleLookback;
}

function PeerDotsBlock({ detail, lookback }: PeerDotsBlockProps) {
  const windowCaption = lookbackOption(lookback).caption;
  const peers = detail.peers;

  // The route already answers fastest-first, and the sort is repeated here on
  // purpose: `rank` is the y coordinate, so an order the payload stopped
  // guaranteeing would not look wrong, it would silently put the dots in the
  // wrong places. Value order is the whole point of the chart.
  const ordered = [...peers].sort((a, b) => a.p50_ms - b.p50_ms);
  const rows: DotRow[] = ordered.map((peer, index) => ({
    rank: index,
    company: peer.company,
    company_name: peer.company_name,
    p50_ms: peer.p50_ms,
    loads: peer.loads,
    is_subject: peer.is_subject,
    weight: peer.is_subject ? SUBJECT_WEIGHT : PEER_WEIGHT,
  }));
  const subjectRows = rows.filter((row) => row.is_subject);
  const peerRows = rows.filter((row) => !row.is_subject);
  const medians = rows.map((row) => row.p50_ms);
  const band = clusterBand(medians);
  const subjectRank = subjectRows[0]?.rank ?? null;
  const gap = gapVerdict(rows, band);
  const subjectMedian = subjectRows[0]?.p50_ms ?? null;

  const config: ChartConfig = {
    [DOT_PEER]: { label: "Other companies", color: PEER_COLOR },
    [DOT_SUBJECT]: { label: `${detail.company_name} (${detail.company})`, color: SUBJECT_COLOR },
  };

  return (
    <Block
      title="Every company running this form"
      caption={`${formatCount(peers.length)} companies · ${windowCaption} · sorted by median, fastest first`}
      icon={<Icon icon={Building2} size="sm" aria-hidden="true" />}
    >
      {rows.length === 0 ? (
        <NotMeasured
          title="No company cleared the load floor"
          description={`A company needs at least 30 loads of ${detail.form} in the ${windowCaption} before its median is worth plotting, and none reached it. That is a thin window, not a fast estate — and a company missing from a plot like this is never a company that was quick.`}
        />
      ) : (
        <>
          {/* Height from the row count, because the estate decides how many
              companies run a form and a fixed height either crushes the ticks or
              leaves half the card empty. `style` is the container's own documented
              sizing API. */}
          <Chart.Container
            config={config}
            style={{ height: Math.max(180, rows.length * 26 + 64) }}
            aria-label={`Median load of ${detail.form} per company, ${formatCount(rows.length)} companies sorted fastest first, from ${formatDurationMs(medians[0])} to ${formatDurationMs(medians[medians.length - 1])}, ${windowCaption}`}
          >
            <Chart.ScatterChart margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              {band && (
                // The cluster, banded from the data: everything below the largest
                // step between two neighbouring medians. Drawn, not asserted —
                // the same rule refuses to draw a band when no step qualifies.
                <Chart.ReferenceArea
                  x1={band.from}
                  x2={band.to}
                  fill="var(--norican-fg-muted)"
                  fillOpacity={0.1}
                  stroke="var(--norican-border-strong)"
                  strokeDasharray="4 4"
                  label={{
                    value: `${band.size} within ${formatDurationCompact(band.to - band.from)}`,
                    position: "insideTopLeft",
                    fill: "var(--norican-fg-muted)",
                    fontSize: 11,
                  }}
                />
              )}
              <Chart.XAxis
                {...Chart.axisProps}
                type="number"
                dataKey="p50_ms"
                // Anchored at zero: this chart is read for the size of the empty
                // space, and a truncated axis would manufacture one.
                domain={[0, Math.max(...medians) * 1.08]}
                tickFormatter={(value: number) => formatDurationCompact(value)}
              />
              {/* A numeric axis carrying the rank, with the company codes as its
                  ticks — because a category axis takes its order from whichever
                  series names a value first, and the value order is the whole
                  point of this chart. */}
              <Chart.YAxis
                {...Chart.axisProps}
                type="number"
                dataKey="rank"
                domain={[-0.5, rows.length - 0.5]}
                ticks={rows.map((row) => row.rank)}
                tickFormatter={(value: number) => rows[value]?.company ?? ""}
                interval={0}
                reversed
                width={54}
              />
              {/* Size, so the finding's own company is not marked by colour
                  alone. The domain is fixed rather than derived: a plot with no
                  subject dot has one weight in its data, and a zero-width domain
                  has no scale to map. */}
              <Chart.ZAxis
                dataKey="weight"
                domain={[PEER_WEIGHT, SUBJECT_WEIGHT]}
                range={[70, 210]}
              />
              {subjectRank != null && (
                <Chart.ReferenceLine
                  y={subjectRank}
                  stroke={SUBJECT_COLOR}
                  strokeDasharray="4 4"
                  strokeWidth={1}
                />
              )}
              <Chart.Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={
                  <Chart.TooltipContent
                    hideLabel
                    rows={(datum: DotRow) => [
                      {
                        label: `${datum.company_name} (${datum.company})`,
                        value: formatDurationMs(datum.p50_ms),
                        color: datum.is_subject ? SUBJECT_COLOR : PEER_COLOR,
                      },
                      { label: "Loads", value: formatCount(datum.loads) },
                      ...comparisonRow(datum, subjectMedian, detail.company),
                    ]}
                  />
                }
              />
              {/* Two series, so a legend (§6). `name` is the config key, which is
                  what the kit's legend and the `--color-*` vars both resolve on. */}
              <Chart.Scatter
                {...Chart.seriesDefaults}
                name={DOT_PEER}
                data={peerRows}
                fill={`var(--color-${DOT_PEER})`}
                shape="circle"
              />
              <Chart.Scatter
                {...Chart.seriesDefaults}
                name={DOT_SUBJECT}
                data={subjectRows}
                fill={`var(--color-${DOT_SUBJECT})`}
                shape="diamond"
              />
              <Chart.Legend content={<Chart.LegendContent />} />
            </Chart.ScatterChart>
          </Chart.Container>

          <Typography variant="caption" tone="muted">
            One dot per legal entity, ordered by median rather than by name — alphabetical order
            would scatter the same numbers at random and hide the shape. Only companies with at
            least 30 loads appear, so a company that is missing is one that barely used the form,
            never one that was quick.{" "}
            {subjectRows.length === 0
              ? `${detail.company_name} itself is below that floor and has no dot here, which is why the finding above cannot be read off this plot.`
              : `${detail.company_name} is the larger diamond.`}
          </Typography>

          {gap && <VerdictFooter verdict={gap} />}
        </>
      )}
    </Block>
  );
}

interface ClusterBand {
  /** Fastest median in the cluster. */
  from: number;
  /** Slowest median still inside the cluster — the near edge of the gap. */
  to: number;
  /** How many companies are in the cluster. */
  size: number;
  /** How many are beyond it. */
  tailSize: number;
  /** The first median past the gap. */
  beyond: number;
  /** How many times the gap multiplies. */
  ratio: number;
  /** Rank of the last company inside the cluster. */
  splitRank: number;
}

/**
 * Split a sorted list of medians into a cluster and a tail at the largest step
 * between two neighbours, measured as a **ratio** so the rule is scale-free: the
 * same code has to work on a form whose companies sit at 1.2 s and on one whose
 * companies sit at 60 s.
 *
 * Returns `null` when there is no gap worth drawing — too few companies, or no
 * step big enough to call one. That branch matters as much as the other: on
 * `SalesQuotationTableForNew` the medians climb evenly from 35 to 80 s, and
 * banding an arbitrary subset of them would invent a finding.
 */
function clusterBand(sorted: readonly number[]): ClusterBand | null {
  if (sorted.length < MIN_DOTS_FOR_BAND) {
    return null;
  }
  let splitRank = -1;
  let ratio = 1;
  for (let index = 0; index < sorted.length - 1; index += 1) {
    const lower = sorted[index] as number;
    const upper = sorted[index + 1] as number;
    if (lower <= 0) {
      continue;
    }
    const step = upper / lower;
    if (step > ratio) {
      ratio = step;
      splitRank = index;
    }
  }
  // `splitRank < 1` would leave one company "clustered" with itself.
  if (splitRank < 1 || ratio < MIN_GAP_RATIO) {
    return null;
  }
  return {
    from: sorted[0] as number,
    to: sorted[splitRank] as number,
    size: splitRank + 1,
    tailSize: sorted.length - splitRank - 1,
    beyond: sorted[splitRank + 1] as number,
    ratio,
    splitRank,
  };
}

/** What the spread of the dots says, including when it says "nothing unusual". */
function gapVerdict(rows: readonly DotRow[], band: ClusterBand | null): Verdict | null {
  if (rows.length < MIN_DOTS_FOR_BAND) {
    return null;
  }
  const slowest = rows[rows.length - 1] as DotRow;
  const fastest = rows[0] as DotRow;
  if (band == null) {
    return {
      label: "No gap",
      tone: "neutral",
      text: `The medians climb evenly from ${formatDurationMs(fastest.p50_ms)} to ${formatDurationMs(slowest.p50_ms)} with no step large enough to separate a cluster from a tail. On this form the cost looks like the form's own, not one company's configuration.`,
    };
  }
  const subject = rows.find((row) => row.is_subject);
  const subjectOutside = subject != null && subject.rank > band.splitRank;
  return {
    label: "Gap",
    tone: subjectOutside ? "danger" : "warning",
    text: `${band.size} companies run this form between ${formatDurationMs(band.from)} and ${formatDurationMs(band.to)}, then nothing until ${formatDurationMs(band.beyond)} — a ${formatRatio(band.ratio)} step with no company inside it, and ${formatCount(band.tailSize)} beyond it. ${
      subjectOutside
        ? `${subject.company_name} is on the far side of that gap, so the form is demonstrably capable of being fast and is not being fast here.`
        : subject == null
          ? "This finding's own company has too few loads to appear, so the gap belongs to the companies that do."
          : `${subject.company_name} sits inside the cluster, so the gap on this form is somebody else's finding.`
    }`,
  };
}

/**
 * The tooltip's third row: how this company stands against the finding's own
 * cell, spelled out in words because a bare `19.2x` does not say which way round
 * it runs. Omitted entirely when the subject has no dot — a ratio against a
 * company that did not clear the load floor would be a number with no meaning.
 */
function comparisonRow(
  datum: DotRow,
  subjectMedian: number | null,
  subjectCompany: string,
): { label: string; value: string }[] {
  if (subjectMedian == null) {
    return [];
  }
  if (datum.is_subject) {
    return [{ label: "This finding", value: "the cell this drawer is about" }];
  }
  if (datum.p50_ms <= 0) {
    return [];
  }
  const ratio = subjectMedian / datum.p50_ms;
  return [
    {
      label: `Against ${subjectCompany}`,
      value: ratio >= 1 ? `${formatRatio(ratio)} faster` : `${formatRatio(1 / ratio)} slower`,
    },
  ];
}

/* ------------------------------------------------------------------------ *
 * c. The hour-of-day strip
 * ------------------------------------------------------------------------ */

/**
 * The five steps of the sequential ramp as static utilities.
 *
 * WORKAROUND (design-system gap): neither `@norican/react` nor
 * `@norican/dataviz` ships a sequential heat strip. `StatusStrip` is the right
 * geometry but its cells take the RAG tones, and status colour is reserved for
 * good/degraded/broken (§3.2) — magnitude is a sequential scale (§3.4), so
 * tinting 24 hours with RAG would assert a verdict per hour that the data does
 * not carry. The cells are therefore plain elements wearing the DS's own
 * `--norican-scale-danger-*` tokens through token-mapped Tailwind utilities:
 * no hex, no `color-mix`, no forked component CSS, and the whole thing collapses
 * to `StatusStrip` the day it grows a sequential variant. The class names are
 * written out rather than built from a template because Tailwind's scanner reads
 * source text, not runtime strings.
 *
 * The danger family is the right hue here for the same reason the per-company
 * grid uses it: the quantity is waiting, and more of it is worse. The bands are
 * intensity, not a judgement on the hour.
 */
const BAND_CLASS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: "bg-scale-danger-1",
  2: "bg-scale-danger-2",
  3: "bg-scale-danger-3",
  4: "bg-scale-danger-4",
  5: "bg-scale-danger-5",
};

/** An hour with no loads: a hole in the strip, visibly not the lowest band. */
const EMPTY_CELL_CLASS = "border-border border border-dashed bg-transparent";

/** Hours that get a printed tick under the strip. */
const HOUR_TICKS = new Set([0, 6, 12, 18]);

interface HourSlot {
  hour: number;
  /** `null` where the hour was not measured — never 0. */
  p50_ms: number | null;
  events: number | null;
}

interface HourStripBlockProps {
  detail: FoConsoleFindingDetail;
  lookback: FoConsoleLookback;
}

function HourStripBlock({ detail, lookback }: HourStripBlockProps) {
  const windowCaption = lookbackOption(lookback).caption;
  const measured = detail.hourly;
  const byHour = new Map<number, FoConsoleFindingHour>(measured.map((row) => [row.hour, row]));
  const slots: HourSlot[] = Array.from({ length: DAY_HOURS }, (_, hour) => {
    const row = byHour.get(hour);
    return { hour, p50_ms: row?.p50_ms ?? null, events: row?.events ?? null };
  });

  const values = measured.map((row) => row.p50_ms);
  // The VERDICT is read only from hours with enough loads to have a median.
  // `events` exists precisely because a median over 300 loads and a median over
  // 3 are not the same statement, and reading peak/trough off p50 alone let two
  // loads at 01:00 become the trough of a 24x "load-driven" spread — a verdict
  // with a ticket routing attached, derived from noise. The strip still DRAWS
  // every measured hour; only the sentence beneath it is restricted.
  const solid = measured.filter((row) => (row.events ?? 0) >= MIN_LOADS_FOR_SHAPE);
  const peak = solid.reduce<FoConsoleFindingHour | null>(
    (worst, row) => (worst == null || row.p50_ms > worst.p50_ms ? row : worst),
    null,
  );
  const trough = solid.reduce<FoConsoleFindingHour | null>(
    (best, row) => (best == null || row.p50_ms < best.p50_ms ? row : best),
    null,
  );
  // Anchored at zero rather than at the fastest hour: normalising min-to-max
  // would stretch a 1.7x day across the whole ramp and paint a rhythm that is
  // not in the data.
  const ceiling = values.length > 0 ? Math.max(...values) : 0;
  // A window shorter than a day cannot contain every hour, so most of the strip
  // is empty by construction rather than by measurement — and "flat" or
  // "peaked" said about four hours of a six-hour window is a claim about the
  // lookback, not about the form. The shape is only read over a full day.
  const fullDay = lookbackOption(lookback).ms >= DAY_MS;
  const rhythm = fullDay ? rhythmVerdict(solid, peak, trough) : null;

  return (
    <Block
      title="Median by hour of day"
      caption={`${formatCount(measured.length)} of ${DAY_HOURS} hours measured · UTC · ${windowCaption}`}
      icon={<Icon icon={Clock} size="sm" aria-hidden="true" />}
    >
      {measured.length === 0 ? (
        <NotMeasured
          title="No hour was measured"
          description={`Nobody opened ${detail.form} in ${detail.company} in the ${windowCaption}, or the hourly query failed — an error above says which. Twenty-four empty cells would say "instant all day", which is the opposite of what an unmeasured day means.`}
        />
      ) : (
        <>
          <div
            role="img"
            aria-label={`Median load of ${detail.form} in ${detail.company_name} by hour of day, UTC. ${formatCount(measured.length)} of ${DAY_HOURS} hours had at least one load; the rest are gaps, not zeroes. Slowest ${formatDurationMs(peak?.p50_ms)} at ${padHour(peak?.hour)}, fastest ${formatDurationMs(trough?.p50_ms)} at ${padHour(trough?.hour)}.`}
            className="flex flex-col gap-1"
          >
            <div className="flex items-stretch gap-px" aria-hidden="true">
              {slots.map((slot) => {
                const band = heatBand(slot.p50_ms, 0, ceiling);
                return (
                  <div
                    key={slot.hour}
                    className={cn(
                      "h-8 flex-1 rounded-sm",
                      band ? BAND_CLASS[band] : EMPTY_CELL_CLASS,
                    )}
                    title={
                      slot.p50_ms == null
                        ? `${padHour(slot.hour)} — no load`
                        : `${padHour(slot.hour)} — ${formatDurationMs(slot.p50_ms)} median over ${formatCount(slot.events)} loads`
                    }
                  />
                );
              })}
            </div>
            <div className="text-fg-muted flex gap-px text-xs" aria-hidden="true">
              {slots.map((slot) => (
                <span key={slot.hour} className="flex-1 text-center tabular-nums">
                  {HOUR_TICKS.has(slot.hour) ? padHour(slot.hour).slice(0, 2) : ""}
                </span>
              ))}
            </div>
          </div>

          <RampKey ceiling={ceiling} />

          <Typography variant="caption" tone="muted">
            Hours are UTC, so a working day sits where the company&apos;s time zone puts it rather
            than in the middle. {GAP_NOT_ZERO} The ramp runs from nothing to the slowest hour
            measured ({formatDurationMs(ceiling)}), not from the fastest hour to the slowest — a day
            that barely moves should look like one.
            {!fullDay &&
              ` This window is ${windowCaption}, shorter than a day, so most of these hours could not have been measured whatever the form does. Widen the lookback before reading the shape of the day off this strip.`}
          </Typography>

          {rhythm && <VerdictFooter verdict={rhythm} />}
        </>
      )}
    </Block>
  );
}

/** The strip's key: the ramp in words as well as colour, plus what a hole means. */
function RampKey({ ceiling }: { ceiling: number }) {
  return (
    <div className="text-fg-muted flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      <span className="flex items-center gap-1.5">
        <span>0</span>
        <span className="flex gap-px" aria-hidden="true">
          {([1, 2, 3, 4, 5] as const).map((band) => (
            <span key={band} className={cn("h-3 w-4 rounded-sm", BAND_CLASS[band])} />
          ))}
        </span>
        <span className="tabular-nums">{formatDurationMs(ceiling)}</span>
      </span>
      <span className="flex items-center gap-1.5">
        <span className={cn("h-3 w-4 rounded-sm", EMPTY_CELL_CLASS)} aria-hidden="true" />
        <span>no load that hour</span>
      </span>
    </div>
  );
}

/** `07:00`, or an em dash where there is no hour to name. */
function padHour(hour: number | null | undefined): string {
  if (hour == null || !Number.isFinite(hour)) {
    return EM_DASH;
  }
  return `${String(hour).padStart(2, "0")}:00`;
}

/**
 * Saw-tooth or flat — the one thing this strip is read for, stated as a
 * conclusion rather than left for the reader to eyeball off a colour ramp.
 *
 * A day measured in fewer than four hours gets no verdict at all: three cells
 * are not a shape, and "flat" said about three cells is a guess wearing a badge.
 * Four is the real floor — the measured `SalesQuotationTableForNew` day has
 * exactly four hours and is genuinely flat across them.
 */
function rhythmVerdict(
  measured: readonly FoConsoleFindingHour[],
  peak: FoConsoleFindingHour | null,
  trough: FoConsoleFindingHour | null,
): Verdict | null {
  if (
    measured.length < MIN_HOURS_FOR_SHAPE ||
    peak == null ||
    trough == null ||
    trough.p50_ms <= 0
  ) {
    return null;
  }
  const spread = peak.p50_ms / trough.p50_ms;
  if (spread < FLAT_SPREAD) {
    return {
      label: "Fixed cost",
      tone: "danger",
      text: `Flat: ${formatDurationMs(peak.p50_ms)} at its worst hour against ${formatDurationMs(trough.p50_ms)} at its best, a ${formatRatio(spread)} spread over a day whose floor never drops below ${formatDurationMs(trough.p50_ms)}. The form costs this much whenever it is opened, so the fix is in the form, not in the schedule.`,
    };
  }
  return {
    label: "Load-driven",
    tone: "warning",
    text: `Peaked: ${formatDurationMs(peak.p50_ms)} at ${padHour(peak.hour)} against ${formatDurationMs(trough.p50_ms)} at ${padHour(trough.hour)}, a ${formatRatio(spread)} spread. The form gets dearer when the system is busy, which points at contention rather than at the form's own work.`,
  };
}

/* ------------------------------------------------------------------------ *
 * Shared chrome
 * ------------------------------------------------------------------------ */

interface BlockProps {
  title: string;
  caption: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
}

/** One panel, in the drawer's own section grammar so the charts read as native. */
function Block({ title, caption, icon, children }: BlockProps) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <Typography
          variant="caption"
          tone="muted"
          as="h3"
          className="flex items-center gap-1.5 font-semibold tracking-wide uppercase"
        >
          {icon}
          {title}
        </Typography>
        <Typography variant="caption" tone="muted">
          {caption}
        </Typography>
      </div>
      {children}
    </section>
  );
}

/**
 * The frame while the payload is in flight. It holds the chart's height so
 * nothing below it moves when the data lands, and says what it is waiting for
 * instead of pulsing (design rules §4.5).
 */
function HoldingFrame({ className }: { className?: string }) {
  return (
    <div
      aria-busy="true"
      className={cn(
        "border-border-subtle text-fg-muted flex items-center justify-center rounded-md border border-dashed",
        className,
      )}
    >
      <Typography variant="caption" tone="muted">
        Reading the workspace
      </Typography>
    </div>
  );
}

/** A measurement that was not taken, said as one — never an empty axis. */
function NotMeasured({ title, description }: { title: string; description: string }) {
  return <EmptyState size="sm" title={title} description={description} />;
}
